import React, { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress, Container, IconButton, Tooltip, Typography } from "@mui/material";
import { Print } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import MonthSelect from "../components/Inputs/MonthSelect";
import CashScenarioPanel from "../components/Reports/CashScenarioPanel";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { cashScenarioReportService, CashScenarioPanel as CashScenarioPanelData } from "../services/reportsService";
import { colors } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";

// Cenário de caixa (decisão do Hilton, 14/09/2026): tela própria só para o painel do mês —
// provisões em aberto a pagar/a receber, saldo em contas e os totais de atendimentos com
// repasse e impostos previstos. Nasceu no Livro-caixa, passou pelo antigo Cenário (hoje
// "Projeção de caixa") e ganhou casa própria para não misturar duas contas na mesma tela.
// Filtro único: a competência. Ação única: imprimir. Lê as MESMAS APIs do Livro-caixa e da
// Análise de Atendimentos.
//
// PADRÃO DESIGN: cabeçalho no painel, filtros em c4u-filters__row, painel único. Sem lista:
// contagem, paginação e colunas não se aplicam (a tela é um painel, não uma listagem).

const currentMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthRange = (month: string): { from: string; to: string } => {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
};

const monthLabel = (month: string): string =>
  new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1).toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  });

// Competências selecionáveis até +12 meses: a provisão já pode estar lançada lá na frente
const maxMonth = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() + 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
})();

const CashScenario: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const [competence, setCompetence] = useState(currentMonth());
  const [panel, setPanel] = useState<CashScenarioPanelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (month: string) => {
    setLoading(true);
    setError(null);
    try {
      const faixa = monthRange(month);
      setPanel(await cashScenarioReportService.panel(faixa.from, faixa.to));
    } catch {
      setPanel(null);
      setError("Não foi possível carregar o cenário de caixa.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(competence);
  }, [competence, load]);

  const hasFilterChanges = competence !== currentMonth();

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 landscape; margin: 8mm; }
        }
      `}</style>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          {/* 1. Cabeçalho: irmão do painel, faixa de cima do cartão */}
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Cenário de caixa
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                O que ainda vai entrar e sair na competência: provisões em aberto, atendimentos do mês
                com repasse e impostos previstos, e o saldo de hoje por conta.
              </Typography>
            </Box>
            <Box className="no-print" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            {/* 3. Barra de filtros: só a competência; a única ação é imprimir */}
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect
                  label="Competência"
                  value={competence}
                  onChange={setCompetence}
                  maxMonth={maxMonth}
                  sx={filterFieldSx}
                />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={() => setCompetence(currentMonth())} disabled={!hasFilterChanges || loading} />
                <Tooltip title="Imprimir o cenário da competência">
                  <span>
                    <IconButton
                      onClick={() => window.print()}
                      disabled={loading || !panel}
                      aria-label="Imprimir"
                      sx={{ width: 40, height: 40, border: "1px solid #ced4da", borderRadius: "4px", color: colors.primary }}
                    >
                      <Print sx={{ fontSize: "1.2rem" }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            {/* Título só no papel: a tela já tem o cabeçalho e o filtro */}
            <Typography sx={{ display: "none", "@media print": { display: "block" }, fontSize: 18, fontWeight: 700, mb: 2 }}>
              Cenário de caixa — {monthLabel(competence)}
            </Typography>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} sx={{ color: colors.primary }} />
              </Box>
            )}

            {!loading && error && (
              <Typography sx={{ textAlign: "center", color: "#dc3545", py: 4 }}>{error}</Typography>
            )}

            {!loading && !error && panel && <CashScenarioPanel panel={panel} monthLabel={monthLabel(competence)} />}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default CashScenario;
